import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, User, LogOut, Settings as SettingsIcon, Users, Plus, Edit, Trash2,
    Search, Calendar, FileText, Lock, Shield, Download, Share2, Eye, EyeOff,
    Upload, X, Crown, AlertTriangle, CheckCircle, Clock, History, Key, 
    UserCheck, Scale, Heart, BookOpen, Archive, FileCheck, Briefcase, Home,
    AlertCircle, Info, ChevronRight, Mail, Phone, MapPin, Link as LinkIcon
} from 'lucide-react';
import { fetchLegacyDocuments, createLegacyDocument, updateLegacyDocument, deleteLegacyDocument } from '../services/legacyApi';
import Premium from './Premium';

const LegacyAndEstate = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('documents'); // documents, instructions, executors, values, wills
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [editingDocument, setEditingDocument] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDocType, setFilterDocType] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [newDocument, setNewDocument] = useState({
        title: '',
        docType: 'Will',
        ownerMemberId: '',
        dateCreated: new Date().toISOString().split('T')[0],
        expiryDate: '',
        jurisdiction: '',
        tags: [],
        status: 'draft',
        summary: '',
        file: null,
        fileName: '',
        fileSize: 0,
        version: 1,
        auditTrail: []
    });

    // Legacy Instructions state
    const [legacyInstructions, setLegacyInstructions] = useState({
        personalMessages: '',
        funeralWishes: '',
        accountNotes: '',
        digitalAccounts: '',
        careInstructions: '',
        financialDirections: '',
        otherWishes: '',
        attachments: []
    });

    // Executors state
    const [executors, setExecutors] = useState([]);
    const [trustedContacts, setTrustedContacts] = useState([]);
    const [legalCounsel, setLegalCounsel] = useState(null);
    const [showAddExecutorModal, setShowAddExecutorModal] = useState(false);
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [newExecutor, setNewExecutor] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'Executor',
        memberId: '',
        isExternal: false,
        permissions: ['view', 'download']
    });

    // Family Values state
    const [familyValues, setFamilyValues] = useState({
        title: 'Our Family Values',
        mission: '',
        stories: '',
        goalsForHeirs: '',
        lastUpdated: new Date().toISOString()
    });

    // Wills state
    const [wills, setWills] = useState([]);
    const [showAddWillModal, setShowAddWillModal] = useState(false);
    const [newWill, setNewWill] = useState({
        title: '',
        dateFinalized: '',
        status: 'draft',
        executorIds: [],
        beneficiaries: '',
        documentId: '',
        witnessInfo: '',
        registrationStatus: '',
        notes: ''
    });

    // Emergency Release state
    const [releaseConfig, setReleaseConfig] = useState({
        triggerType: 'manual',
        requiredVerifiers: 2,
        verifiers: [],
        releaseAttempts: []
    });

    // Load data
    useEffect(() => {
        loadFamilies();
        loadDocuments();
        loadLegacyInstructions();
        loadExecutors();
        loadFamilyValues();
        loadWills();
        loadReleaseConfig();
    }, []);

    const loadFamilies = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to load families');
            }
            
            const familiesData = await response.json();
            setFamilies(familiesData);

            const selectedFamilyId = localStorage.getItem('selectedFamilyId');
            let familyToSelect = null;
            
            if (selectedFamilyId && familiesData.length > 0) {
                familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
            }
            
            if (!familyToSelect && familiesData.length === 1) {
                familyToSelect = familiesData[0];
                localStorage.setItem('selectedFamilyId', familiesData[0].id);
            }
            
            if (!familyToSelect && familiesData.length > 0) {
                localStorage.removeItem('selectedFamilyId');
            }
            
            setSelectedFamily(familyToSelect);
        } catch (error) {
            console.error('Error loading families:', error);
        }
    };

    const loadDocuments = () => {
        const stored = localStorage.getItem('legacyDocuments');
        if (stored) {
            setDocuments(JSON.parse(stored));
        }
    };

    const loadLegacyInstructions = () => {
        const stored = localStorage.getItem('legacyInstructions');
        if (stored) {
            setLegacyInstructions(JSON.parse(stored));
        }
    };

    const loadExecutors = () => {
        const storedExecutors = localStorage.getItem('legacyExecutors');
        const storedContacts = localStorage.getItem('legacyTrustedContacts');
        const storedCounsel = localStorage.getItem('legacyLegalCounsel');
        
        if (storedExecutors) setExecutors(JSON.parse(storedExecutors));
        if (storedContacts) setTrustedContacts(JSON.parse(storedContacts));
        if (storedCounsel) setLegalCounsel(JSON.parse(storedCounsel));
    };

    const loadFamilyValues = () => {
        const stored = localStorage.getItem('familyValues');
        if (stored) {
            setFamilyValues(JSON.parse(stored));
        }
    };

    const loadWills = () => {
        const stored = localStorage.getItem('legacyWills');
        if (stored) {
            setWills(JSON.parse(stored));
        }
    };

    const loadReleaseConfig = () => {
        const stored = localStorage.getItem('legacyReleaseConfig');
        if (stored) {
            setReleaseConfig(JSON.parse(stored));
        }
    };

    // NEW: Load legacy data from API when family changes
    useEffect(() => {
        const loadLegacyData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchLegacyDocuments(selectedFamily.id);
                const items = response.items || [];
                
                // Update state with API data
                const docs = items.filter(i => i.document_type);
                if (docs.length > 0) setDocuments(docs);
            } catch (error) {
                console.error('Error loading legacy data:', error);
            }
        };
        loadLegacyData();
    }, [selectedFamily]);

    const saveDocuments = (data) => {
        localStorage.setItem('legacyDocuments', JSON.stringify(data));
        setDocuments(data);
    };

    const saveLegacyInstructions = (data) => {
        localStorage.setItem('legacyInstructions', JSON.stringify(data));
        setLegacyInstructions(data);
    };

    const saveExecutors = (data) => {
        localStorage.setItem('legacyExecutors', JSON.stringify(data));
        setExecutors(data);
    };

    const saveTrustedContacts = (data) => {
        localStorage.setItem('legacyTrustedContacts', JSON.stringify(data));
        setTrustedContacts(data);
    };

    const saveFamilyValues = (data) => {
        localStorage.setItem('familyValues', JSON.stringify(data));
        setFamilyValues(data);
    };

    const saveWills = (data) => {
        localStorage.setItem('legacyWills', JSON.stringify(data));
        setWills(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Document functions
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only PDF, JPEG, PNG, and DOCX files are allowed');
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewDocument({
                    ...newDocument,
                    file: reader.result,
                    fileName: file.name,
                    fileSize: file.size
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadDocument = () => {
        if (!newDocument.title.trim()) {
            alert('Please enter a document title');
            return;
        }

        if (!newDocument.file) {
            alert('Please select a file to upload');
            return;
        }

        const doc = {
            ...newDocument,
            id: Date.now(),
            uploadedAt: new Date().toISOString(),
            uploadedBy: 'Current User', // In production, use actual user ID
            auditTrail: [
                {
                    action: 'uploaded',
                    by: 'Current User',
                    timestamp: new Date().toISOString()
                }
            ]
        };

        saveDocuments([...documents, doc]);
        resetDocumentForm();
        setShowUploadModal(false);
    };

    const handleUpdateDocument = () => {
        const updated = documents.map(d => {
            if (d.id === editingDocument.id) {
                return {
                    ...newDocument,
                    id: editingDocument.id,
                    version: editingDocument.version + 1,
                    auditTrail: [
                        ...editingDocument.auditTrail,
                        {
                            action: 'updated',
                            by: 'Current User',
                            timestamp: new Date().toISOString()
                        }
                    ]
                };
            }
            return d;
        });
        saveDocuments(updated);
        resetDocumentForm();
        setShowUploadModal(false);
        setEditingDocument(null);
    };

    const handleDeleteDocument = (id) => {
        if (window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
            saveDocuments(documents.filter(d => d.id !== id));
        }
    };

    const resetDocumentForm = () => {
        setNewDocument({
            title: '',
            docType: 'Will',
            ownerMemberId: '',
            dateCreated: new Date().toISOString().split('T')[0],
            expiryDate: '',
            jurisdiction: '',
            tags: [],
            status: 'draft',
            summary: '',
            file: null,
            fileName: '',
            fileSize: 0,
            version: 1,
            auditTrail: []
        });
    };

    // Instructions functions
    const handleSaveInstructions = () => {
        saveLegacyInstructions({
            ...legacyInstructions,
            lastUpdated: new Date().toISOString()
        });
        alert('Legacy instructions saved successfully');
    };

    // Executor functions
    const handleAddExecutor = () => {
        if (!newExecutor.name.trim()) {
            alert('Please enter executor name');
            return;
        }

        const executor = {
            ...newExecutor,
            id: Date.now(),
            addedAt: new Date().toISOString()
        };

        saveExecutors([...executors, executor]);
        setNewExecutor({
            name: '',
            email: '',
            phone: '',
            role: 'Executor',
            memberId: '',
            isExternal: false,
            permissions: ['view', 'download']
        });
        setShowAddExecutorModal(false);
    };

    const handleDeleteExecutor = (id) => {
        if (window.confirm('Are you sure you want to remove this executor?')) {
            saveExecutors(executors.filter(e => e.id !== id));
        }
    };

    // Will functions
    const handleAddWill = () => {
        if (!newWill.title.trim()) {
            alert('Please enter will title');
            return;
        }

        const will = {
            ...newWill,
            id: Date.now(),
            createdAt: new Date().toISOString()
        };

        saveWills([...wills, will]);
        setNewWill({
            title: '',
            dateFinalized: '',
            status: 'draft',
            executorIds: [],
            beneficiaries: '',
            documentId: '',
            witnessInfo: '',
            registrationStatus: '',
            notes: ''
        });
        setShowAddWillModal(false);
    };

    const handleDeleteWill = (id) => {
        if (window.confirm('Are you sure you want to delete this will record?')) {
            saveWills(wills.filter(w => w.id !== id));
        }
    };

    // Filter documents
    const filteredDocuments = documents.filter(doc => {
        const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              doc.summary.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = filterDocType === 'All' || doc.docType === filterDocType;
        const matchesStatus = filterStatus === 'All' || doc.status === filterStatus;
        return matchesSearch && matchesType && matchesStatus;
    });

    const docTypes = ['Will', 'Life Insurance', 'Trust Documents', 'Deeds', 'Power of Attorney', 
                      'Funeral Instructions', 'Account Lists', 'Password Vault', 'Other'];
    const statuses = ['draft', 'active', 'archived'];
    const roles = ['Executor', 'Trusted Contact', 'Legal Counsel', 'Beneficiary'];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
            {/* Legal Disclaimer Banner */}
            <div className="bg-amber-50 border-b-2 border-amber-200">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                            <strong>Legal Disclaimer:</strong> This tool is for organizational purposes only and is not a substitute for professional legal advice. 
                            Please consult with a qualified attorney for all legal matters related to wills, estates, and legacy planning.
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Bar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200 z-50">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Legacy & Estate</span>
                </div>

                {/* Header with Security Badge */}
                <div className="mb-8">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-2">🏛️ Legacy & Estate</h1>
                            <p className="text-gray-600">Secure your family's future with organized legacy planning</p>
                        </div>
                        <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                            <Shield className="w-5 h-5" />
                            <span className="text-sm font-semibold">End-to-End Encrypted</span>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-8 border-b border-gray-200">
                    <div className="flex space-x-8 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('documents')}
                            className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                activeTab === 'documents' ? 'text-primary-blue' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            📁 Document Vault
                            {activeTab === 'documents' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('instructions')}
                            className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                activeTab === 'instructions' ? 'text-primary-blue' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🧾 Legacy Instructions
                            {activeTab === 'instructions' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('executors')}
                            className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                activeTab === 'executors' ? 'text-primary-blue' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🧭 Executors & Access
                            {activeTab === 'executors' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('values')}
                            className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                activeTab === 'values' ? 'text-primary-blue' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🏷️ Family Values
                            {activeTab === 'values' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('wills')}
                            className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                activeTab === 'wills' ? 'text-primary-blue' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            ⚖️ Wills & Legal
                            {activeTab === 'wills' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                    </div>
                </div>

                {/* DOCUMENTS TAB */}
                {activeTab === 'documents' && (
                    <div>
                        {/* Search and Filters */}
                        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                            <div className="flex items-center space-x-4 flex-1">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search documents..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>
                                <select
                                    value={filterDocType}
                                    onChange={(e) => setFilterDocType(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    <option value="All">All Types</option>
                                    {docTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    <option value="All">All Status</option>
                                    {statuses.map(status => (
                                        <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    resetDocumentForm();
                                    setEditingDocument(null);
                                    setShowUploadModal(true);
                                }}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <Upload className="w-5 h-5" />
                                <span>Upload Document</span>
                            </button>
                        </div>

                        {/* Documents List */}
                        <div className="space-y-4">
                            {filteredDocuments.map((doc) => (
                                <div key={doc.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <Lock className="w-5 h-5 text-indigo-600" />
                                                <h3 className="text-xl font-bold text-gray-900">{doc.title}</h3>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                    doc.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    doc.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {doc.status}
                                                </span>
                                                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                                    {doc.docType}
                                                </span>
                                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                                    v{doc.version}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                                                <span className="flex items-center">
                                                    <Calendar className="w-4 h-4 mr-1" />
                                                    {new Date(doc.dateCreated).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center">
                                                    <FileText className="w-4 h-4 mr-1" />
                                                    {doc.fileName}
                                                </span>
                                                <span>
                                                    {(doc.fileSize / 1024).toFixed(2)} KB
                                                </span>
                                                {doc.jurisdiction && (
                                                    <span className="flex items-center">
                                                        <MapPin className="w-4 h-4 mr-1" />
                                                        {doc.jurisdiction}
                                                    </span>
                                                )}
                                            </div>
                                            {doc.summary && (
                                                <p className="text-gray-700 text-sm mb-2">{doc.summary}</p>
                                            )}
                                            {doc.auditTrail && doc.auditTrail.length > 0 && (
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <button className="text-xs text-indigo-600 hover:underline flex items-center">
                                                        <History className="w-3 h-3 mr-1" />
                                                        View Audit Trail ({doc.auditTrail.length} entries)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                <Eye className="w-5 h-5 text-gray-600" />
                                            </button>
                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                <Download className="w-5 h-5 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingDocument(doc);
                                                    setNewDocument(doc);
                                                    setShowUploadModal(true);
                                                }}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-5 h-5 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Trash2 className="w-5 h-5 text-red-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredDocuments.length === 0 && (
                            <div className="text-center py-12">
                                <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No documents found</p>
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="text-indigo-600 hover:underline"
                                >
                                    Upload your first document
                                </button>
                            </div>
                        )}

                        {/* Export Section */}
                        <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Export & Share</h3>
                            <div className="flex space-x-4">
                                <button className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                                    <Download className="w-5 h-5" />
                                    <span>Export All (Encrypted ZIP)</span>
                                </button>
                                <button className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                                    <FileText className="w-5 h-5" />
                                    <span>Generate Legacy Summary PDF</span>
                                </button>
                                <button className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                    <Share2 className="w-5 h-5" />
                                    <span>Create Share Link</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* LEGACY INSTRUCTIONS TAB */}
                {activeTab === 'instructions' && (
                    <div>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                            <div className="flex items-start space-x-3">
                                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-800">
                                    These "When I'm Gone" instructions will help your family understand your wishes. All sections are optional and can be updated anytime.
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {[
                                { key: 'personalMessages', title: '💌 Personal Messages', placeholder: 'Share your love, wisdom, and final messages to family members...' },
                                { key: 'funeralWishes', title: '🕯️ Funeral Wishes', placeholder: 'Describe your preferences for funeral arrangements, burial/cremation, ceremony details...' },
                                { key: 'accountNotes', title: '🔐 Account & Access Notes', placeholder: 'List important accounts, where to find passwords, who to contact...' },
                                { key: 'digitalAccounts', title: '💻 Digital Accounts', placeholder: 'Social media, email, cloud storage accounts and how to access or close them...' },
                                { key: 'careInstructions', title: '👶 Care Instructions (Children/Pets)', placeholder: 'Guardianship preferences, care routines, important information...' },
                                { key: 'financialDirections', title: '💰 Financial Directions', placeholder: 'Bank accounts, investments, debts, insurance policies, advisors to contact...' },
                                { key: 'otherWishes', title: '📝 Other Wishes', placeholder: 'Any other instructions or wishes you want to share...' }
                            ].map(section => (
                                <div key={section.key} className="bg-white rounded-lg shadow-md p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">{section.title}</h3>
                                    <textarea
                                        value={legacyInstructions[section.key]}
                                        onChange={(e) => setLegacyInstructions({ ...legacyInstructions, [section.key]: e.target.value })}
                                        rows={6}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        placeholder={section.placeholder}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                onClick={handleSaveInstructions}
                                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
                            >
                                <CheckCircle className="w-5 h-5" />
                                <span>Save Instructions</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* EXECUTORS TAB */}
                {activeTab === 'executors' && (
                    <div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                            {/* Executors Section */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                        <UserCheck className="w-6 h-6 mr-2 text-indigo-600" />
                                        Executors
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setNewExecutor({ ...newExecutor, role: 'Executor' });
                                            setShowAddExecutorModal(true);
                                        }}
                                        className="flex items-center space-x-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span>Add</span>
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {executors.map(executor => (
                                        <div key={executor.id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-900">{executor.name}</p>
                                                <p className="text-sm text-gray-600">{executor.email}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {executor.permissions.join(', ')}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteExecutor(executor.id)}
                                                className="p-2 hover:bg-red-100 rounded"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    ))}
                                    {executors.length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No executors added yet</p>
                                    )}
                                </div>
                            </div>

                            {/* Trusted Contacts Section */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                        <Users className="w-6 h-6 mr-2 text-green-600" />
                                        Trusted Contacts
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setNewExecutor({ ...newExecutor, role: 'Trusted Contact' });
                                            setShowAddExecutorModal(true);
                                        }}
                                        className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span>Add</span>
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {trustedContacts.map(contact => (
                                        <div key={contact.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-900">{contact.name}</p>
                                                <p className="text-sm text-gray-600">{contact.email}</p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Remove this trusted contact?')) {
                                                        saveTrustedContacts(trustedContacts.filter(c => c.id !== contact.id));
                                                    }
                                                }}
                                                className="p-2 hover:bg-red-100 rounded"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    ))}
                                    {trustedContacts.length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No trusted contacts added yet</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Emergency Release Configuration */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <AlertCircle className="w-6 h-6 mr-2 text-red-600" />
                                Emergency Release Configuration
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Type</label>
                                    <select
                                        value={releaseConfig.triggerType}
                                        onChange={(e) => setReleaseConfig({ ...releaseConfig, triggerType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="manual">Manual Verification by Admin</option>
                                        <option value="death_certificate">Death Certificate Upload</option>
                                        <option value="multi_party">Multi-Party Confirmation</option>
                                        <option value="probate">Probate Initiation</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Required Verifiers (for Multi-Party)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={releaseConfig.requiredVerifiers}
                                        onChange={(e) => setReleaseConfig({ ...releaseConfig, requiredVerifiers: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 p-4 bg-red-50 rounded-lg">
                                <p className="text-sm text-red-800">
                                    <strong>Note:</strong> Emergency release provides controlled access to executors upon verification. 
                                    All release attempts are logged and require proper authentication.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* FAMILY VALUES TAB */}
                {activeTab === 'values' && (
                    <div>
                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">✨ Create Your Family Legacy Statement</h2>
                            <p className="text-gray-700">
                                Document your family's values, mission, and wisdom to pass on to future generations.
                            </p>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                <input
                                    type="text"
                                    value={familyValues.title}
                                    onChange={(e) => setFamilyValues({ ...familyValues, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="e.g., The Smith Family Values"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Mission & Values</label>
                                <textarea
                                    value={familyValues.mission}
                                    onChange={(e) => setFamilyValues({ ...familyValues, mission: e.target.value })}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="What principles guide your family? What do you stand for?"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Stories & Examples</label>
                                <textarea
                                    value={familyValues.stories}
                                    onChange={(e) => setFamilyValues({ ...familyValues, stories: e.target.value })}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Share stories that illustrate your family values in action..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Goals for Future Generations</label>
                                <textarea
                                    value={familyValues.goalsForHeirs}
                                    onChange={(e) => setFamilyValues({ ...familyValues, goalsForHeirs: e.target.value })}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="What do you hope your descendants will achieve or carry forward?"
                                />
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t">
                                <span className="text-sm text-gray-600">
                                    Last updated: {new Date(familyValues.lastUpdated).toLocaleDateString()}
                                </span>
                                <button
                                    onClick={() => {
                                        saveFamilyValues({ ...familyValues, lastUpdated: new Date().toISOString() });
                                        alert('Family values saved successfully!');
                                    }}
                                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
                                >
                                    <Heart className="w-5 h-5" />
                                    <span>Save Family Values</span>
                                </button>
                            </div>
                        </div>

                        {/* Preview Card */}
                        {familyValues.mission && (
                            <div className="mt-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-8 border-2 border-purple-200">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{familyValues.title}</h3>
                                <div className="prose max-w-none">
                                    <p className="text-gray-800 whitespace-pre-wrap">{familyValues.mission}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* WILLS TAB */}
                {activeTab === 'wills' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Will Management & Legal Workflow</h2>
                            <button
                                onClick={() => setShowAddWillModal(true)}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-slate-600 to-indigo-600 text-white rounded-lg hover:opacity-90"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Add Will Record</span>
                            </button>
                        </div>

                        <div className="space-y-4">
                            {wills.map(will => (
                                <div key={will.id} className="bg-white rounded-lg shadow-md p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <Scale className="w-6 h-6 text-slate-600" />
                                                <h3 className="text-xl font-bold text-gray-900">{will.title}</h3>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                    will.status === 'finalized' ? 'bg-green-100 text-green-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {will.status}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                                                {will.dateFinalized && (
                                                    <div className="flex items-center">
                                                        <Calendar className="w-4 h-4 mr-1" />
                                                        Finalized: {new Date(will.dateFinalized).toLocaleDateString()}
                                                    </div>
                                                )}
                                                <div>
                                                    Registration: {will.registrationStatus || 'Not registered'}
                                                </div>
                                            </div>
                                            {will.beneficiaries && (
                                                <div className="mb-2">
                                                    <strong className="text-sm">Beneficiaries:</strong>
                                                    <p className="text-sm text-gray-700">{will.beneficiaries}</p>
                                                </div>
                                            )}
                                            {will.notes && (
                                                <p className="text-sm text-gray-600 mt-2">{will.notes}</p>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                <FileText className="w-5 h-5 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteWill(will.id)}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                <Trash2 className="w-5 h-5 text-red-600" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Legal Checklist */}
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <h4 className="font-semibold text-gray-900 mb-2">Legal Checklist</h4>
                                        <div className="space-y-2">
                                            {[
                                                'Will drafted and reviewed',
                                                'Witnesses signatures obtained',
                                                'Notarization completed',
                                                'Will registered with authorities',
                                                'Executor(s) informed',
                                                'Copy provided to legal counsel'
                                            ].map((item, idx) => (
                                                <label key={idx} className="flex items-center space-x-2 text-sm">
                                                    <input type="checkbox" className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                                                    <span>{item}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {wills.length === 0 && (
                            <div className="text-center py-12 bg-white rounded-lg shadow-md">
                                <Scale className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No will records yet</p>
                                <button
                                    onClick={() => setShowAddWillModal(true)}
                                    className="text-indigo-600 hover:underline"
                                >
                                    Add your first will record
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Upload Document Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold flex items-center">
                                <Lock className="w-6 h-6 mr-2 text-indigo-600" />
                                {editingDocument ? 'Edit Document' : 'Upload Secure Document'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setEditingDocument(null);
                                    resetDocumentForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                                <input
                                    type="text"
                                    value={newDocument.title}
                                    onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="e.g., Last Will and Testament - 2025"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                                    <select
                                        value={newDocument.docType}
                                        onChange={(e) => setNewDocument({ ...newDocument, docType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                        {docTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={newDocument.status}
                                        onChange={(e) => setNewDocument({ ...newDocument, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    >
                                        {statuses.map(status => (
                                            <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
                                    <input
                                        type="date"
                                        value={newDocument.dateCreated}
                                        onChange={(e) => setNewDocument({ ...newDocument, dateCreated: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                                    <input
                                        type="date"
                                        value={newDocument.expiryDate}
                                        onChange={(e) => setNewDocument({ ...newDocument, expiryDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Jurisdiction</label>
                                <input
                                    type="text"
                                    value={newDocument.jurisdiction}
                                    onChange={(e) => setNewDocument({ ...newDocument, jurisdiction: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="e.g., California, USA"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Summary</label>
                                <textarea
                                    value={newDocument.summary}
                                    onChange={(e) => setNewDocument({ ...newDocument, summary: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="Brief description of document contents..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Upload File * (PDF, JPEG, PNG, DOCX - Max 10MB)</label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.docx"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300"
                                    >
                                        <Upload className="w-5 h-5" />
                                        <span>Choose File</span>
                                    </button>
                                    {newDocument.fileName && (
                                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                                            <FileCheck className="w-4 h-4 text-green-600" />
                                            <span>{newDocument.fileName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <p className="text-xs text-amber-800">
                                    <strong>Security Note:</strong> All documents are encrypted at rest and in transit. 
                                    Only authorized family members and designated executors can access these files.
                                </p>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setEditingDocument(null);
                                    resetDocumentForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingDocument ? handleUpdateDocument : handleUploadDocument}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
                            >
                                <Lock className="w-4 h-4" />
                                <span>{editingDocument ? 'Update' : 'Upload'} Securely</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Executor/Contact Modal */}
            {showAddExecutorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-lg w-full">
                        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold">Add {newExecutor.role}</h2>
                            <button
                                onClick={() => setShowAddExecutorModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={newExecutor.name}
                                    onChange={(e) => setNewExecutor({ ...newExecutor, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="Full name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={newExecutor.email}
                                    onChange={(e) => setNewExecutor({ ...newExecutor, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="email@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={newExecutor.phone}
                                    onChange={(e) => setNewExecutor({ ...newExecutor, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="+1 (555) 123-4567"
                                />
                            </div>

                            {newExecutor.role === 'Executor' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                                    <div className="space-y-2">
                                        {['view', 'download', 'edit', 'share'].map(perm => (
                                            <label key={perm} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={newExecutor.permissions.includes(perm)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setNewExecutor({
                                                                ...newExecutor,
                                                                permissions: [...newExecutor.permissions, perm]
                                                            });
                                                        } else {
                                                            setNewExecutor({
                                                                ...newExecutor,
                                                                permissions: newExecutor.permissions.filter(p => p !== perm)
                                                            });
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
                                                />
                                                <span className="text-sm capitalize">{perm}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => setShowAddExecutorModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddExecutor}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                                Add {newExecutor.role}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Will Modal */}
            {showAddWillModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold flex items-center">
                                <Scale className="w-6 h-6 mr-2 text-slate-600" />
                                Add Will Record
                            </h2>
                            <button
                                onClick={() => setShowAddWillModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Will Title *</label>
                                <input
                                    type="text"
                                    value={newWill.title}
                                    onChange={(e) => setNewWill({ ...newWill, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    placeholder="e.g., Primary Last Will and Testament"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Finalized</label>
                                    <input
                                        type="date"
                                        value={newWill.dateFinalized}
                                        onChange={(e) => setNewWill({ ...newWill, dateFinalized: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={newWill.status}
                                        onChange={(e) => setNewWill({ ...newWill, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="finalized">Finalized</option>
                                        <option value="registered">Registered</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiaries</label>
                                <textarea
                                    value={newWill.beneficiaries}
                                    onChange={(e) => setNewWill({ ...newWill, beneficiaries: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    placeholder="List beneficiaries and their allocations..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Witness Information</label>
                                <textarea
                                    value={newWill.witnessInfo}
                                    onChange={(e) => setNewWill({ ...newWill, witnessInfo: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    placeholder="Witness names, signatures, dates..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Status</label>
                                <input
                                    type="text"
                                    value={newWill.registrationStatus}
                                    onChange={(e) => setNewWill({ ...newWill, registrationStatus: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    placeholder="e.g., Registered with County Clerk, File #12345"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={newWill.notes}
                                    onChange={(e) => setNewWill({ ...newWill, notes: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                                    placeholder="Additional notes or instructions..."
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => setShowAddWillModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddWill}
                                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
                            >
                                Add Will Record
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegacyAndEstate;

